import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Devise } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
export const TYPES_PROVENANCE = ['proprietaire', 'exposition', 'publication', 'mention'] as const;
export const MODES_ACQUISITION = [
  'achat',
  'heritage',
  'don',
  'commande',
  'atelier',
  'inconnu',
] as const;
export const CATEGORIES_COUT = [
  'fabrication',
  'fonderie',
  'tirage',
  'encadrement',
  'transport',
  'restauration',
  'autre',
] as const;
export interface ProvenancePresentee {
  id: string;
  oeuvreId: string;
  exemplaireId: string | null;
  rang: number;
  type: (typeof TYPES_PROVENANCE)[number];
  libelle: string;
  contactId: string | null;
  contactNom: string | null;
  periode: string;
  mode: (typeof MODES_ACQUISITION)[number] | null;
  source: string;
  public: boolean;
  note: string;
  ligne: string;
  pieces?: string[];
  piecesMasquees?: boolean;
  verification?: {
    etat: 'a_verifier' | 'verifiee' | 'contestee';
    par: string;
    le: string;
    note: string;
  } | null;
}
export type CorpsProvenance = Pick<ProvenancePresentee, 'type' | 'libelle'> &
  Partial<
    Pick<
      ProvenancePresentee,
      'contactId' | 'periode' | 'mode' | 'source' | 'public' | 'note' | 'pieces'
    >
  > & { exemplaireId?: string };
export interface VenteConnue {
  origine: 'vente_simple' | 'facture';
  contactId: string;
  contactNom: string;
  date: string;
  reference: string | null;
}
export interface CoutPresente {
  id: string;
  exemplaireId: string | null;
  categorie: (typeof CATEGORIES_COUT)[number];
  libelle: string;
  montantCentimes: number;
  devise: Devise;
  date: string | null;
  fournisseur: string;
  note: string;
}
export type CorpsCout = Pick<CoutPresente, 'categorie' | 'libelle' | 'montantCentimes'> &
  Partial<Pick<CoutPresente, 'devise' | 'date' | 'fournisseur' | 'note'>> & {
    exemplaireId?: string;
  };
const chemin = (g: string, id: string) =>
  `/g/${encodeURIComponent(g)}/oeuvres/${encodeURIComponent(id)}`;
export const provenanceApi = {
  rapport: (g: string, id: string, a?: AppelApi) =>
    api.get<{ url: string }>(`${chemin(g, id)}/provenance/rapport.pdf`, { galerie: g, ...a }),
  verifier: (
    g: string,
    id: string,
    p: string,
    c: { etat: 'a_verifier' | 'verifiee' | 'contestee'; note: string },
    a?: AppelApi,
  ) =>
    api.post(`${chemin(g, id)}/provenance/${encodeURIComponent(p)}/verification`, c, {
      galerie: g,
      ...a,
    }),
  lire: (g: string, id: string, a?: AppelApi) =>
    api.get<{
      entrees: ProvenancePresentee[];
      provenance: { etapes: number; verifiees: number; contestees: number; sansPiece: number };
      lignesPubliques: string[];
      ventesConnues: VenteConnue[];
    }>(`${chemin(g, id)}/provenance`, { galerie: g, ...a }),
  ajouter: (g: string, id: string, c: CorpsProvenance, a?: AppelApi) =>
    api.post(`${chemin(g, id)}/provenance`, c, { galerie: g, ...a }),
  modifier: (g: string, id: string, p: string, c: Partial<CorpsProvenance>, a?: AppelApi) =>
    api.patch(`${chemin(g, id)}/provenance/${encodeURIComponent(p)}`, c, { galerie: g, ...a }),
  retirer: (g: string, id: string, p: string, a?: AppelApi) =>
    api.delete(`${chemin(g, id)}/provenance/${encodeURIComponent(p)}`, { galerie: g, ...a }),
  ordonner: (g: string, id: string, ids: string[], a?: AppelApi) =>
    api.put(`${chemin(g, id)}/provenance/ordre`, { ids }, { galerie: g, ...a }),
  couts: (g: string, id: string, a?: AppelApi) =>
    api.get<{ couts: CoutPresente[]; totaux: { devise: Devise; totalCentimes: number }[] }>(
      `${chemin(g, id)}/couts`,
      { galerie: g, ...a },
    ),
  ajouterCout: (g: string, id: string, c: CorpsCout, a?: AppelApi) =>
    api.post(`${chemin(g, id)}/couts`, c, { galerie: g, ...a }),
  modifierCout: (g: string, id: string, p: string, c: Partial<CorpsCout>, a?: AppelApi) =>
    api.patch(`${chemin(g, id)}/couts/${encodeURIComponent(p)}`, c, { galerie: g, ...a }),
  retirerCout: (g: string, id: string, p: string, a?: AppelApi) =>
    api.delete(`${chemin(g, id)}/couts/${encodeURIComponent(p)}`, { galerie: g, ...a }),
};
export function useProvenance(g: string, id: string, actif = true) {
  return useQuery({
    queryKey: cleMetier(g, 'provenance', id),
    queryFn: (c) => provenanceApi.lire(g, id, appelDeRequete(c)),
    enabled: actif,
  });
}
export function useCouts(g: string, id: string, actif: boolean) {
  return useQuery({
    queryKey: cleMetier(g, 'couts', id),
    queryFn: (c) => provenanceApi.couts(g, id, appelDeRequete(c)),
    enabled: actif,
  });
}
function useGeste<T, R>(g: string, fn: (v: T, a: AppelApi) => Promise<R>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: fn,
      onSuccess: () => {
        for (const collection of ['provenance', 'couts'])
          void client.invalidateQueries({ queryKey: cleMetier(g, collection) });
      },
    }),
  );
}
export const useAjouterProvenance = (g: string, id: string) =>
  useGeste(g, (c: CorpsProvenance, a) => provenanceApi.ajouter(g, id, c, a));
export const useModifierProvenance = (g: string, id: string) =>
  useGeste(g, (v: { id: string; corps: Partial<CorpsProvenance> }, a) =>
    provenanceApi.modifier(g, id, v.id, v.corps, a),
  );
export const useRetirerProvenance = (g: string, id: string) =>
  useGeste(g, (p: string, a) => provenanceApi.retirer(g, id, p, a));
export const useOrdonnerProvenance = (g: string, id: string) =>
  useGeste(g, (ids: string[], a) => provenanceApi.ordonner(g, id, ids, a));
export const useAjouterCout = (g: string, id: string) =>
  useGeste(g, (c: CorpsCout, a) => provenanceApi.ajouterCout(g, id, c, a));
export const useModifierCout = (g: string, id: string) =>
  useGeste(g, (v: { id: string; corps: Partial<CorpsCout> }, a) =>
    provenanceApi.modifierCout(g, id, v.id, v.corps, a),
  );
export const useRetirerCout = (g: string, id: string) =>
  useGeste(g, (p: string, a) => provenanceApi.retirerCout(g, id, p, a));

export const useVerifierProvenance = (g: string, id: string) =>
  useGeste(g, (v: { id: string; etat: 'a_verifier' | 'verifiee' | 'contestee'; note: string }, a) =>
    provenanceApi.verifier(g, id, v.id, { etat: v.etat, note: v.note }, a),
  );
export const useRapportProvenance = (g: string, id: string) =>
  useMutation(dansLaPortee({ mutationFn: (_v: void, a) => provenanceApi.rapport(g, id, a) }));
